// Auto-play schedule for the Music Studio.
//
// Stored locally (no backend endpoint yet) so the in-page scheduler can read it
// synchronously. The config picks a set of library tracks and a daily time
// window on chosen weekdays; while the Music Studio page is open and "now"
// falls inside the window, the selected tracks auto-play in order.
//
// Shape: { enabled, fromTime: "HH:MM", toTime: "HH:MM", days: number[] (0=Sun
// … 6=Sat), trackIds: (string|number)[] }.

export const MUSIC_AUTOPLAY_KEY = "musicLibrary:autoPlay";

export const DEFAULT_AUTOPLAY = {
    enabled: false,
    fromTime: "",
    toTime: "",
    days: [],
    trackIds: [],
};

export const readAutoPlaySettings = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(MUSIC_AUTOPLAY_KEY));
        if (!parsed || typeof parsed !== "object") {
            return {...DEFAULT_AUTOPLAY};
        }
        return {
            enabled: Boolean(parsed.enabled),
            fromTime: typeof parsed.fromTime === "string" ? parsed.fromTime : "",
            toTime: typeof parsed.toTime === "string" ? parsed.toTime : "",
            days: Array.isArray(parsed.days) ? parsed.days.map(Number).filter((d) => d >= 0 && d <= 6) : [],
            trackIds: Array.isArray(parsed.trackIds) ? parsed.trackIds : [],
        };
    } catch (e) {
        return {...DEFAULT_AUTOPLAY};
    }
};

export const writeAutoPlaySettings = (settings = DEFAULT_AUTOPLAY) => {
    try {
        localStorage.setItem(MUSIC_AUTOPLAY_KEY, JSON.stringify(settings));
    } catch (e) {
        // localStorage full / unavailable — non-fatal, the schedule just won't
        // persist across reloads.
    }
};

// "HH:MM" -> minutes since midnight, or null when malformed.
const toMinutes = (time) => {
    if (typeof time !== "string" || !/^\d{1,2}:\d{2}$/.test(time)) return null;
    const [h, m] = time.split(":").map(Number);
    if (h > 23 || m > 59) return null;
    return h * 60 + m;
};

// Is `now` inside the configured window on a matching weekday? Supports windows
// that wrap past midnight (toTime <= fromTime). An empty `days` means every day.
export const isWithinSchedule = (settings, now = new Date()) => {
    if (!settings?.enabled || !settings.trackIds?.length) return false;
    const from = toMinutes(settings.fromTime);
    const to = toMinutes(settings.toTime);
    if (from === null || to === null) return false;

    const minutes = now.getHours() * 60 + now.getMinutes();
    const dayOk = !settings.days?.length || settings.days.includes(now.getDay());
    if (!dayOk) return false;

    // Same-day window (from < to) vs. overnight wrap (from >= to).
    return from < to ? minutes >= from && minutes < to : minutes >= from || minutes < to;
};
